/**
 * Auto-Add Free Gift - Adopt & Protect Collection
 *
 * When a customer adds a product from the "adoptandprotect" collection to cart,
 * automatically add a free Reef-Safe Sunscreen product.
 * Limited to one free gift per order.
 */

class AutoAddFreeGift {
  constructor(config) {
    this.config = {
      freeGiftVariantId: config.freeGiftVariantId,
      freeGiftProductHandle: config.freeGiftProductHandle || 'reef-safe-sunscreen',
      eligibleProductIds: config.eligibleProductIds || [],
      giftPropertyKey: '_free_gift_from',
      giftPropertyValue: 'adoptandprotect',
      ...config
    };

    this.isAddingGift = false;
    this.init();
  }

  init() {
    // Listen for cart add events
    document.addEventListener('on:cart:add', this.handleCartAdd.bind(this));
    document.addEventListener('on:line-item:change', this.handleLineItemChange.bind(this));

    // Also intercept direct cart/add.js calls
    this.interceptCartAdd();
  }

  /**
   * Intercept fetch calls to /cart/add.js to catch all add-to-cart actions
   */
  interceptCartAdd() {
    const originalFetch = window.fetch;
    const self = this;

    window.fetch = function(url, options) {
      const promise = originalFetch.apply(this, arguments);

      // Check if this is a cart add request
      if (typeof url === 'string' && url.includes('/cart/add')) {
        promise.then(response => {
          // Clone response to read it without consuming
          response.clone().json().then(data => {
            if (data && data.product_id) {
              self.checkAndAddFreeGift(data.product_id);
            }
          }).catch(() => {});
        }).catch(() => {});
      }

      return promise;
    };
  }

  /**
   * Handle on:cart:add event
   */
  async handleCartAdd(event) {
    const { cart, variantId } = event.detail;

    // Find the product that was just added
    const addedItem = cart.items.find(item => item.variant_id === variantId);
    if (addedItem) {
      await this.checkAndAddFreeGift(addedItem.product_id);
    }
  }

  /**
   * Handle line item changes (in case product is removed)
   */
  async handleLineItemChange(event) {
    const { cart, variantId, newQuantity } = event.detail;

    // If an eligible product was removed, consider removing the free gift
    if (newQuantity === 0) {
      await this.checkIfShouldRemoveGift(cart);
    }
  }

  /**
   * Check if the added product is eligible and add free gift
   */
  async checkAndAddFreeGift(productId) {
    // Prevent multiple simultaneous additions
    if (this.isAddingGift) return;

    // Check if product is eligible (in the adoptandprotect collection)
    if (!this.isEligibleProduct(productId)) {
      return;
    }

    // Check current cart state
    const cart = await this.getCart();

    // Check if free gift is already in cart
    if (this.hasFreeGiftInCart(cart)) {
      console.log('[AutoAddFreeGift] Free gift already in cart');
      return;
    }

    // Check if there's an eligible product in cart (might have been removed)
    if (!this.hasEligibleProductInCart(cart)) {
      console.log('[AutoAddFreeGift] No eligible products in cart');
      return;
    }

    // Add the free gift
    await this.addFreeGift();
  }

  /**
   * Check if product ID is in the eligible list
   */
  isEligibleProduct(productId) {
    return this.config.eligibleProductIds.includes(Number(productId));
  }

  /**
   * Check if cart already has the free gift
   */
  hasFreeGiftInCart(cart) {
    return cart.items.some(item => {
      // Check by variant ID
      if (item.variant_id === this.config.freeGiftVariantId) {
        return true;
      }
      // Check by product handle
      if (item.handle === this.config.freeGiftProductHandle) {
        return true;
      }
      // Check by gift property
      if (item.properties && item.properties[this.config.giftPropertyKey]) {
        return true;
      }
      return false;
    });
  }

  /**
   * Check if cart has any eligible products
   */
  hasEligibleProductInCart(cart) {
    return cart.items.some(item => this.isEligibleProduct(item.product_id));
  }

  /**
   * Get current cart state
   */
  async getCart() {
    const response = await fetch('/cart.js', {
      headers: { 'Accept': 'application/json' }
    });
    return response.json();
  }

  /**
   * Add the free gift to cart
   */
  async addFreeGift() {
    this.isAddingGift = true;

    try {
      console.log('[AutoAddFreeGift] Adding free gift...');

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          id: this.config.freeGiftVariantId,
          quantity: 1,
          properties: {
            [this.config.giftPropertyKey]: this.config.giftPropertyValue,
            '_is_free_gift': 'true'
          }
        })
      });

      if (!response.ok) {
        throw new Error('Failed to add free gift');
      }

      const data = await response.json();
      console.log('[AutoAddFreeGift] Free gift added successfully', data);
 
      // Refresh cart drawer/page
      this.refreshCart();

      // Show notification
      this.showNotification();

    } catch (error) {
      console.error('[AutoAddFreeGift] Error adding free gift:', error);
    } finally {
      this.isAddingGift = false;
    }
  }

  /**
   * Check if we should remove the free gift (when all eligible products removed)
   */
  async checkIfShouldRemoveGift(cart) {
    // If no eligible products remain but free gift is still there, remove it
    if (!this.hasEligibleProductInCart(cart) && this.hasFreeGiftInCart(cart)) {
      await this.removeFreeGift(cart);
    }
  }

  /**
   * Remove the free gift from cart
   */
  async removeFreeGift(cart) {
    const giftItem = cart.items.find(item =>
      item.variant_id === this.config.freeGiftVariantId ||
      item.handle === this.config.freeGiftProductHandle ||
      (item.properties && item.properties[this.config.giftPropertyKey])
    );

    if (!giftItem) return;

    try {
      console.log('[AutoAddFreeGift] Removing free gift...');

      await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          id: giftItem.key,
          quantity: 0
        })
      });

      this.refreshCart();

    } catch (error) {
      console.error('[AutoAddFreeGift] Error removing free gift:', error);
    }
  }
 
  /**
   * Refresh the cart UI
   */
  refreshCart() {
    // Dispatch cart refresh event
    document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', { bubbles: true }));

    // Also try direct refresh for cart page
    const cartItems = document.querySelector('cart-items');
    if (cartItems && typeof cartItems.refresh === 'function') {
      cartItems.refresh();
    }

    // Update cart count
    this.updateCartCount();
  }

  /**
   * Update cart count in header
   */
  async updateCartCount() {
    try {
      const cart = await this.getCart();

      const countElements = document.querySelectorAll(
        '[data-cart-count], .cart-count, .js-cart-count, .header__cart-count, [data-cart-item-count]'
      );

      countElements.forEach(el => {
        el.textContent = cart.item_count;
      });
    } catch (error) {
      console.error('[AutoAddFreeGift] Error updating cart count:', error);
    }
  }

  /**
   * Show notification that free gift was added
   */
  showNotification() {
    // Create and show a notification
    const notification = document.createElement('div');
    notification.className = 'free-gift-notification';
    notification.innerHTML = `
      <div class="free-gift-notification__content">
        <span class="free-gift-notification__icon">🎁</span>
        <span class="free-gift-notification__text">Free Reef-Safe Sunscreen added to your cart!</span>
      </div>
    `;

    document.body.appendChild(notification);

    // Animate in
    requestAnimationFrame(() => {
      notification.classList.add('is-visible');
    });

    // Remove after delay
    setTimeout(() => {
      notification.classList.remove('is-visible');
      setTimeout(() => notification.remove(), 300);
    }, 4000);
  }
}

// Initialize when config is available
function initAutoAddFreeGift() {
  if (window.autoAddFreeGiftConfig) {
    window.autoAddFreeGift = new AutoAddFreeGift(window.autoAddFreeGiftConfig);
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initAutoAddFreeGift);
} else {
  initAutoAddFreeGift();
}
